<script setup>
import { computed, onMounted, toRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useBroadcastComposer } from 'dashboard/composables/useBroadcastComposer';
import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmButton from 'dashboard/components-next/button/ConfirmButton.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import MessageEditor from 'dashboard/components-next/NewConversation/components/MessageEditor.vue';
import BroadcastPreview from './BroadcastPreview.vue';
import BroadcastSourceCard from './BroadcastSourceCard.vue';

const props = defineProps({
  telegramInboxes: {
    type: Array,
    default: () => [],
  },
  teams: {
    type: Array,
    default: () => [],
  },
  blacklistVersion: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['close', 'created']);
const { t } = useI18n();

const {
  title,
  message,
  options,
  inboxId,
  conversationFilters,
  sources,
  preview,
  templates,
  selectedTemplateId,
  templateName,
  isPreviewing,
  isLaunching,
  isLoadingTemplates,
  isSavingTemplate,
  isDeletingTemplate,
  canPreview,
  canLaunch,
  canSaveTemplate,
  addTeamSource,
  addIdentifierSource,
  updateConversationFilter,
  updateSource,
  removeSource,
  moveSource,
  confirmIdentifierSource,
  previewTargets,
  fetchTemplates,
  loadTemplate,
  saveTemplate,
  updateTemplate,
  deleteTemplate,
  launchBroadcast,
  resetComposer,
} = useBroadcastComposer(toRef(props, 'blacklistVersion'));

watch(
  () => props.blacklistVersion,
  () => {
    preview.value = null;
    sources.value.forEach(source => {
      if (source.type !== 'identifiers') return;
      source.resolution = null;
      source.confirmedSignature = '';
    });
  }
);

const hasIncludeSource = computed(() =>
  sources.value.some(source => source.operation === 'include')
);

const courierAppOptions = computed(() => [
  {
    value: 'boltfood',
    label: t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.APPS.BOLTFOOD'),
  },
  {
    value: 'ubereats',
    label: t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.APPS.UBEREATS'),
  },
  {
    value: 'glovo',
    label: t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.APPS.GLOVO'),
  },
  {
    value: 'wolt',
    label: t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.APPS.WOLT'),
  },
  {
    value: 'stuart',
    label: t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.APPS.STUART'),
  },
]);

const languageOptions = computed(() => [
  {
    value: 'en',
    label: t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.LANGUAGES.EN'),
  },
  {
    value: 'ru',
    label: t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.LANGUAGES.RU'),
  },
  {
    value: 'pl',
    label: t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.LANGUAGES.PL'),
  },
  {
    value: 'uk',
    label: t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.LANGUAGES.UK'),
  },
]);

const selectedCourierApps = computed(
  () => conversationFilters.value.courier_apps || []
);
const selectedLanguages = computed(
  () => conversationFilters.value.languages || []
);

const handleFilterToggle = (filterName, value, isSelected) => {
  const selectedValues = conversationFilters.value[filterName] || [];
  updateConversationFilter(
    filterName,
    isSelected
      ? [...selectedValues, value]
      : selectedValues.filter(selectedValue => selectedValue !== value)
  );
};

const nonEditableConversationFilterNames = [
  'statuses',
  'labels_all',
  'labels_any',
];
const activeConversationFilters = computed(() =>
  nonEditableConversationFilterNames
    .map(filterName => [
      filterName,
      conversationFilters.value[filterName] || [],
    ])
    .filter(([, values]) => values.length)
);

const apiErrorMessage = (error, fallbackKey) =>
  error.response?.data?.message || t(fallbackKey);

const handleFetchTemplates = async () => {
  try {
    await fetchTemplates();
  } catch (error) {
    useAlert(
      apiErrorMessage(error, 'BROADCASTS.COMPOSER.ALERTS.TEMPLATES_FAILED')
    );
  }
};

const handleConfirmSource = async source => {
  try {
    await confirmIdentifierSource(source);
  } catch (error) {
    useAlert(
      apiErrorMessage(error, 'BROADCASTS.COMPOSER.ALERTS.RESOLUTION_FAILED')
    );
  }
};

const handlePreview = async () => {
  try {
    await previewTargets();
  } catch (error) {
    useAlert(
      apiErrorMessage(error, 'BROADCASTS.COMPOSER.ALERTS.PREVIEW_FAILED')
    );
  }
};

const handleLoadTemplate = async () => {
  if (!selectedTemplateId.value) return;
  try {
    await loadTemplate(selectedTemplateId.value);
    useAlert(t('BROADCASTS.COMPOSER.ALERTS.TEMPLATE_LOADED'));
  } catch (error) {
    useAlert(
      apiErrorMessage(error, 'BROADCASTS.COMPOSER.ALERTS.TEMPLATE_LOAD_FAILED')
    );
  }
};

const handleSaveTemplate = async () => {
  try {
    await saveTemplate();
    useAlert(t('BROADCASTS.COMPOSER.ALERTS.TEMPLATE_SAVED'));
  } catch (error) {
    useAlert(
      apiErrorMessage(error, 'BROADCASTS.COMPOSER.ALERTS.TEMPLATE_SAVE_FAILED')
    );
  }
};

const handleUpdateTemplate = async () => {
  try {
    await updateTemplate();
    useAlert(t('BROADCASTS.COMPOSER.ALERTS.TEMPLATE_UPDATED'));
  } catch (error) {
    useAlert(
      apiErrorMessage(
        error,
        'BROADCASTS.COMPOSER.ALERTS.TEMPLATE_UPDATE_FAILED'
      )
    );
  }
};

const handleDeleteTemplate = async () => {
  try {
    await deleteTemplate();
    useAlert(t('BROADCASTS.COMPOSER.ALERTS.TEMPLATE_DELETED'));
  } catch (error) {
    useAlert(
      apiErrorMessage(
        error,
        'BROADCASTS.COMPOSER.ALERTS.TEMPLATE_DELETE_FAILED'
      )
    );
  }
};

const handleLaunch = async () => {
  try {
    await launchBroadcast();
    useAlert(t('BROADCASTS.COMPOSER.ALERTS.LAUNCHED'));
    resetComposer();
    emit('created');
  } catch (error) {
    useAlert(
      apiErrorMessage(error, 'BROADCASTS.COMPOSER.ALERTS.LAUNCH_FAILED')
    );
  }
};

onMounted(handleFetchTemplates);
</script>

<template>
  <section
    class="mb-8 flex flex-col gap-6 rounded-xl border border-n-strong bg-n-alpha-1 p-5 shadow-sm"
  >
    <div class="flex items-start justify-between gap-4">
      <div>
        <h2 class="text-lg font-medium text-n-slate-12">
          {{ t('BROADCASTS.COMPOSER.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('BROADCASTS.COMPOSER.DESCRIPTION') }}
        </p>
      </div>
      <Button
        type="button"
        icon="i-lucide-x"
        color="slate"
        variant="ghost"
        :aria-label="t('BROADCASTS.COMPOSER.CLOSE')"
        @click="emit('close')"
      />
    </div>

    <div
      class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
    >
      <div>
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('BROADCASTS.COMPOSER.TEMPLATES.TITLE') }}
        </h3>
        <p class="mt-1 text-xs text-n-slate-10">
          {{ t('BROADCASTS.COMPOSER.TEMPLATES.DESCRIPTION') }}
        </p>
      </div>

      <div class="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
        <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
          {{ t('BROADCASTS.COMPOSER.TEMPLATES.SAVED_TEMPLATE') }}
          <select
            v-model="selectedTemplateId"
            class="h-10 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            :disabled="isLoadingTemplates"
          >
            <option value="">
              {{ t('BROADCASTS.COMPOSER.TEMPLATES.SELECT') }}
            </option>
            <option
              v-for="broadcastTemplate in templates"
              :key="broadcastTemplate.id"
              :value="broadcastTemplate.id"
            >
              {{ broadcastTemplate.name }}
            </option>
          </select>
        </label>
        <div class="flex flex-wrap items-end gap-2">
          <Button
            type="button"
            color="slate"
            variant="outline"
            :label="t('BROADCASTS.COMPOSER.TEMPLATES.LOAD')"
            :disabled="!selectedTemplateId || isLoadingTemplates"
            @click="handleLoadTemplate"
          />
          <Button
            v-if="isDeletingTemplate"
            type="button"
            color="ruby"
            variant="ghost"
            :label="t('BROADCASTS.COMPOSER.TEMPLATES.DELETE')"
            is-loading
            disabled
          />
          <ConfirmButton
            v-else-if="selectedTemplateId"
            type="button"
            color="ruby"
            variant="ghost"
            :label="t('BROADCASTS.COMPOSER.TEMPLATES.DELETE')"
            :confirm-label="t('BROADCASTS.COMPOSER.TEMPLATES.CONFIRM_DELETE')"
            @click="handleDeleteTemplate"
          />
          <Button
            v-else
            type="button"
            color="ruby"
            variant="ghost"
            :label="t('BROADCASTS.COMPOSER.TEMPLATES.DELETE')"
            disabled
          />
        </div>
      </div>

      <div class="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
        <Input
          v-model="templateName"
          :label="t('BROADCASTS.COMPOSER.TEMPLATES.NAME')"
          :placeholder="t('BROADCASTS.COMPOSER.TEMPLATES.NAME_PLACEHOLDER')"
        />
        <div class="flex flex-wrap items-end gap-2">
          <Button
            type="button"
            :label="t('BROADCASTS.COMPOSER.TEMPLATES.SAVE_NEW')"
            :is-loading="isSavingTemplate && !selectedTemplateId"
            :disabled="!canSaveTemplate"
            @click="handleSaveTemplate"
          />
          <Button
            type="button"
            color="slate"
            variant="outline"
            :label="t('BROADCASTS.COMPOSER.TEMPLATES.UPDATE')"
            :is-loading="isSavingTemplate && Boolean(selectedTemplateId)"
            :disabled="!selectedTemplateId || !canSaveTemplate"
            @click="handleUpdateTemplate"
          />
        </div>
      </div>
    </div>

    <div class="grid gap-4 md:grid-cols-2">
      <Input
        v-model="title"
        :label="t('BROADCASTS.COMPOSER.BROADCAST_TITLE')"
        :placeholder="t('BROADCASTS.COMPOSER.BROADCAST_TITLE_PLACEHOLDER')"
        :message="t('BROADCASTS.COMPOSER.BROADCAST_TITLE_HELP')"
      />
      <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
        {{ t('BROADCASTS.COMPOSER.INBOX') }}
        <select
          v-model="inboxId"
          class="h-10 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
        >
          <option value="">
            {{ t('BROADCASTS.COMPOSER.INBOX_PLACEHOLDER') }}
          </option>
          <option
            v-for="inbox in telegramInboxes"
            :key="inbox.id"
            :value="inbox.id"
          >
            {{ inbox.name }}
          </option>
        </select>
        <span class="mt-1 text-xs font-normal text-n-slate-10">
          {{ t('BROADCASTS.COMPOSER.INBOX_HELP') }}
        </span>
      </label>
    </div>

    <section
      class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-2 p-4"
    >
      <div>
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.TITLE') }}
        </h3>
        <p class="mt-1 text-xs text-n-slate-10">
          {{ t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.SCOPE_HELP') }}
        </p>
      </div>

      <div class="grid gap-4 lg:grid-cols-2">
        <fieldset
          class="flex flex-col gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-4"
        >
          <legend class="px-1 text-sm font-medium text-n-slate-12">
            {{ t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.APPS_TITLE') }}
          </legend>
          <p class="text-xs text-n-slate-10">
            {{ t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.APPS_HELP') }}
          </p>
          <div class="grid gap-2 sm:grid-cols-2">
            <label
              v-for="app in courierAppOptions"
              :key="app.value"
              class="flex items-center gap-2 text-sm text-n-slate-12"
            >
              <input
                type="checkbox"
                class="size-4 rounded border-n-strong text-n-brand focus:ring-n-brand"
                :checked="selectedCourierApps.includes(app.value)"
                @change="
                  handleFilterToggle(
                    'courier_apps',
                    app.value,
                    $event.target.checked
                  )
                "
              />
              {{ app.label }}
            </label>
          </div>
        </fieldset>

        <fieldset
          class="flex flex-col gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-4"
        >
          <legend class="px-1 text-sm font-medium text-n-slate-12">
            {{ t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.LANGUAGES_TITLE') }}
          </legend>
          <p class="text-xs text-n-slate-10">
            {{ t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.LANGUAGES_HELP') }}
          </p>
          <div class="grid gap-2 sm:grid-cols-2">
            <label
              v-for="language in languageOptions"
              :key="language.value"
              class="flex items-center gap-2 text-sm text-n-slate-12"
            >
              <input
                type="checkbox"
                class="size-4 rounded border-n-strong text-n-brand focus:ring-n-brand"
                :checked="selectedLanguages.includes(language.value)"
                @change="
                  handleFilterToggle(
                    'languages',
                    language.value,
                    $event.target.checked
                  )
                "
              />
              {{ language.label }}
            </label>
          </div>
        </fieldset>
      </div>
    </section>

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('BROADCASTS.COMPOSER.MESSAGE') }}
      </label>
      <div class="min-h-52 rounded-xl border border-n-weak bg-n-solid-2">
        <MessageEditor
          v-model="message.content"
          :is-email-or-web-widget-inbox="false"
        />
      </div>
      <p class="text-xs text-n-slate-10">
        {{ t('BROADCASTS.COMPOSER.MESSAGE_HELP') }}
      </p>
      <div class="grid gap-4 md:grid-cols-2">
        <div class="flex flex-col gap-1">
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input
              v-model="message.content_attributes.silent"
              type="checkbox"
              class="size-4 rounded border-n-strong text-n-brand focus:ring-n-brand"
            />
            {{ t('BROADCASTS.COMPOSER.SILENT') }}
          </label>
          <span class="text-xs text-n-slate-10">
            {{ t('BROADCASTS.COMPOSER.SILENT_HELP') }}
          </span>
        </div>
        <div class="flex flex-col gap-1">
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            {{ t('BROADCASTS.COMPOSER.THROTTLE') }}
            <input
              v-model.number="options.throttle_per_second"
              type="number"
              min="1"
              max="20"
              class="h-9 w-20 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            />
          </label>
          <span class="text-xs text-n-slate-10">
            {{ t('BROADCASTS.COMPOSER.THROTTLE_HELP') }}
          </span>
        </div>
      </div>
    </div>

    <div
      v-if="activeConversationFilters.length"
      class="rounded-lg border border-n-amber-7 bg-n-amber-2 p-3"
    >
      <div class="text-xs font-medium text-n-slate-12">
        {{ t('BROADCASTS.COMPOSER.LOADED_FILTERS') }}
      </div>
      <div class="mt-2 flex flex-wrap gap-2">
        <span
          v-for="[filter, values] in activeConversationFilters"
          :key="filter"
          class="rounded-md bg-n-solid-2 px-2 py-1 text-xs text-n-slate-11"
        >
          {{ filter }}: {{ values.join(', ') }}
        </span>
      </div>
    </div>

    <div class="flex flex-col gap-4">
      <div class="flex flex-wrap items-center gap-2">
        <div class="mr-auto">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('BROADCASTS.COMPOSER.AUDIENCE') }}
          </h3>
          <p class="mt-1 text-xs text-n-slate-10">
            {{ t('BROADCASTS.COMPOSER.SET_SEMANTICS') }}
          </p>
        </div>
        <Button
          type="button"
          color="slate"
          variant="outline"
          icon="i-lucide-users"
          :label="t('BROADCASTS.COMPOSER.ADD_TEAM')"
          @click="addTeamSource"
        />
        <Button
          type="button"
          color="slate"
          variant="outline"
          icon="i-lucide-clipboard-paste"
          :label="t('BROADCASTS.COMPOSER.ADD_IDENTIFIERS')"
          @click="addIdentifierSource"
        />
      </div>

      <div
        v-if="!sources.length"
        class="rounded-xl border border-dashed border-n-strong p-6 text-center text-sm text-n-slate-10"
      >
        {{ t('BROADCASTS.COMPOSER.NO_SOURCES') }}
      </div>

      <BroadcastSourceCard
        v-for="(source, sourceIndex) in sources"
        :key="source.key"
        :source="source"
        :teams="teams"
        :inbox-selected="Number(inboxId) > 0"
        :is-first="sourceIndex === 0"
        :is-last="sourceIndex === sources.length - 1"
        @update="updateSource(sourceIndex, $event)"
        @remove="removeSource(sourceIndex)"
        @move-up="moveSource(sourceIndex, -1)"
        @move-down="moveSource(sourceIndex, 1)"
        @confirm="handleConfirmSource(source)"
      />

      <div
        v-if="sources.length && !hasIncludeSource"
        class="rounded-lg bg-n-ruby-3 p-3 text-sm text-n-ruby-11"
      >
        {{ t('BROADCASTS.COMPOSER.INCLUDE_REQUIRED') }}
      </div>
    </div>

    <div class="flex justify-end">
      <Button
        type="button"
        color="slate"
        variant="outline"
        icon="i-lucide-scan-search"
        :label="t('BROADCASTS.COMPOSER.PREVIEW.ACTION')"
        :is-loading="isPreviewing"
        :disabled="!canPreview"
        @click="handlePreview"
      />
    </div>

    <BroadcastPreview v-if="preview" :preview="preview" />

    <div class="flex flex-col items-end gap-2 border-t border-n-strong pt-5">
      <Button
        type="button"
        size="lg"
        icon="i-lucide-send"
        :label="t('BROADCASTS.COMPOSER.LAUNCH')"
        :is-loading="isLaunching"
        :disabled="!canLaunch"
        @click="handleLaunch"
      />
      <span class="text-xs text-n-slate-10">
        {{ t('BROADCASTS.COMPOSER.LAUNCH_HELP') }}
      </span>
    </div>
  </section>
</template>
